import React from 'react';
import type { DocumentPageListItem } from '@avisoma//shared';
import clsx from 'clsx';
import { twMerge } from 'tailwind-merge';
import { DocumentPageMarkdownColumn } from './DocumentPageMarkdownColumn';
import { DocumentPageMetadataAdvancedColumn } from './DocumentPageMetadataAdvancedColumn';
import { DocumentPagePdfColumn } from './DocumentPagePdfColumn';
import { DocumentPageProcessingSidebar } from './DocumentPageProcessingSidebar';

export interface DocumentPageRowProps {
  /** The parent document's id (the only document field this row's subtree reads). */
  documentId: string;
  page: DocumentPageListItem;
  /** 1-based position in the full page list, computed once by the parent (was an O(n) indexOf per row). */
  pageNumber: number;
  /** Ordered ids of every page; ONE shared reference across all rows (was rebuilt per row). */
  pageIds: string[];
  showAdvancedView: boolean;
  /** Whether this row's action dropdown is the open one (per-row boolean, not the global id → memo-friendly). */
  isDropdownOpen: boolean;
  /** Stable toggle: opens this row's dropdown, or closes it if it's already the open one. */
  onDropdownToggle: (pageId: string) => void;
  requestRelad: () => void;
  strandedStage?: string;
}

// DR-2426: memoized so a dropdown toggle (or any parent re-render that leaves this row's
// props referentially unchanged) re-renders only the affected rows, not all N. The parent
// hands down narrow, stable props — `documentId` instead of the whole document object, a
// per-row `isDropdownOpen` boolean instead of the global open id, a shared `pageIds`
// reference, and useCallback-stable handlers — so the default shallow prop compare holds.
function DocumentPageRowComponent({
  documentId,
  page,
  pageNumber,
  pageIds,
  showAdvancedView,
  isDropdownOpen,
  onDropdownToggle,
  requestRelad,
  strandedStage,
}: DocumentPageRowProps) {
  return (
    <div className="p-4">
      <div className={twMerge(clsx('grid grid-cols-4 gap-6', !showAdvancedView && 'grid-cols-3'))}>
        <DocumentPagePdfColumn pageId={page.id} pageNumberLabel={String(pageNumber)} pageIds={pageIds} />

        {showAdvancedView && <DocumentPageMetadataAdvancedColumn page={page} />}

        <DocumentPageMarkdownColumn page={page} showAdvancedView={showAdvancedView} />

        <DocumentPageProcessingSidebar
          documentId={documentId}
          page={page}
          isDropdownOpen={isDropdownOpen}
          onDropdownToggle={onDropdownToggle}
          requestRelad={requestRelad}
          strandedStage={strandedStage}
        />
      </div>
    </div>
  );
}

export const DocumentPageRow = React.memo(DocumentPageRowComponent);
